import React, { useState, useContext, useEffect } from 'react';
import { I18nReactContext } from '../../../../../contexts/I18nContext';
import { CourseEditorContext } from '../../../../../contexts/CourseEditorContext';
import { getCompanyVideoBankAPI } from '../../../../../Services/api';
import { getPublicAssetsAPI } from '../../../../../Services/PublicAssets_api';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import ErrorIcon from '@mui/icons-material/Error';
import RefreshIcon from '@mui/icons-material/Refresh';
import './ChapterEditor.scss';

export const VideoComponent = ({ 
    id,
    content,
    signedUrl,
    isSelected, 
    onSelect,
    borderRadius,
    backgroundColor,
    opacity = 1,
    mode = "edit",
    onVideoComplete,
    courseCompleted = false,
    isTemplateMode = false,
    onAnimationComplete,
    isSequentialAnimationActive = false,
    onOpenEditorDrawer
}) => {
    const { i18n } = useContext(I18nReactContext);
    const courseEditorContext = useContext(CourseEditorContext);
    const [isAIAnimating, setIsAIAnimating] = useState(false);

    // This temporary URL is used to display the video immediately after upload
    // while still storing the CompanyFile ID in the content attribute.
    // The server will convert the ID to a signed URL when it returns the course data.
    const [tempVideoUrl, setTempVideoUrl] = useState(null);

    // State to track failed video loads and prevent infinite retries
    const [videoLoadError, setVideoLoadError] = useState(false);
    const [retryAttempts, setRetryAttempts] = useState(0);
    const [failedVideoIds, setFailedVideoIds] = useState(new Set());

    // State to track when we're loading a video from the bank to prevent flickering
    const [isLoadingFromBank, setIsLoadingFromBank] = useState(false);
    const [currentContentBeingLoaded, setCurrentContentBeingLoaded] = useState(null);


    // Check if content is a video key and try to load it from the video bank
    useEffect(() => {
        // Only proceed if content exists, is not a URL, we don't already have a signedUrl, and this video hasn't already failed to load
        // Skip loading from bank if we already have a signedUrl (mimics background video pattern)
        if (content && !content.startsWith('http') && !signedUrl && !failedVideoIds.has(content)) {
            console.log("Content appears to be a video key, trying to load from video bank:", content);

            // If we're already loading this same content, don't start another load
            if (currentContentBeingLoaded === content) {
                console.log("Already loading this content, skipping:", content);
                return;
            }

            // Only clear tempVideoUrl if the content has actually changed to a different video
            // and we don't already have a valid tempVideoUrl for this content
            if (currentContentBeingLoaded !== content) {
                console.log("Content changed, starting to load new video:", content);
                setCurrentContentBeingLoaded(content);
                setIsLoadingFromBank(true);
                // Only clear tempVideoUrl if we're switching to a completely different video
                if (tempVideoUrl && currentContentBeingLoaded && currentContentBeingLoaded !== content) {
                    console.log("Clearing previous tempVideoUrl for different video");
                    setTempVideoUrl(null);
                }
            }

            const loadVideoFromBank = async () => {
                try {
                    let videoFound = false;

                    if (isTemplateMode) {
                        // In template mode, use the public assets API
                        console.log("Loading public video from bank for template mode with key:", content);
                        const response = await getPublicAssetsAPI({
                            paginationModel: { page: 0, pageSize: 5 },
                            filterModel: {
                                filterValues: {
                                    type: 'VIDEO'
                                }
                            },
                            sortModel: { field: 'createdAt', sort: 'desc' }
                        });

                        // Find the video with the matching key
                        if (response && response.data) {
                            const video = response.data.find(asset => asset.id === content);
                            if (video && video.signedUrl) {
                                console.log("Found video in public bank with matching key, using signed URL:", video.signedUrl);
                                setTempVideoUrl(video.signedUrl);
                                setVideoLoadError(false);
                                videoFound = true;
                            }
                        }
                    } else {
                        // In regular mode, use the company video bank API
                        const videos = await getCompanyVideoBankAPI();
                        const video = videos.find(vid => vid.key === content);
                        if (video && video.signedUrl) {
                            console.log("Found video in company bank with matching key, using signed URL:", video.signedUrl);
                            setTempVideoUrl(video.signedUrl);
                            setVideoLoadError(false);
                            videoFound = true;
                        }
                    }

                    // If video was not found, mark it as failed to prevent infinite retries
                    if (!videoFound) {
                        console.warn("Video not found in bank with key:", content);
                        setFailedVideoIds(prev => new Set([...prev, content]));
                        setVideoLoadError(true);
                    }
                } catch (error) {
                    console.error("Error loading video from bank:", error);
                    // Mark this video as failed to prevent infinite retries
                    setFailedVideoIds(prev => new Set([...prev, content]));
                    setVideoLoadError(true);
                } finally {
                    // Always clear loading state when done
                    setIsLoadingFromBank(false);
                }
            };

            loadVideoFromBank();
        }
    }, [content, signedUrl, failedVideoIds, isTemplateMode]);

    // Clear error states when content changes to a new video
    useEffect(() => {
        if (content) {
            // Clear error states when content changes
            setVideoLoadError(false);
            setRetryAttempts(0);
            // Don't clear failedVideoIds here as we want to track failed videos across content changes
        }
    }, [content]);

    // Only clear the temporary URL when the component is unmounted
    // We don't want to clear it when content changes, as we need it for displaying videos from the video bank
    useEffect(() => {
        // Clean up function to clear the temporary URL when the component is unmounted
        return () => {
            setTempVideoUrl(null);
        };
    }, []);

    // AI Video Animation Effect
    useEffect(() => {
        const isAIGenerating = courseEditorContext?.isGeneratingChapter;
        const isShowingAIAnimations = courseEditorContext?.isShowingAIChapterAnimations;
        const hasVideo = content && (content.startsWith('http') || tempVideoUrl);

        // Only animate if (AI is generating OR showing AI chapter animations OR sequential animations are active) and we have video content and we're not in edit mode
        if ((isAIGenerating || isShowingAIAnimations || isSequentialAnimationActive) && hasVideo && mode !== "edit") {
            setIsAIAnimating(true);

            // For sequential animations during AI generation, trigger completion almost immediately
            // For other cases, use the full 2-second animation duration
            const completionDelay = isSequentialAnimationActive ? 200 : 2000;

            // Stop animation after the appropriate delay
            const animationTimeout = setTimeout(() => {
                setIsAIAnimating(false);
                // Call animation completion callback for sequential animations
                if (isSequentialAnimationActive && onAnimationComplete) {
                    console.log('VideoComponent animation completed for:', id);
                    onAnimationComplete(id);
                }
            }, completionDelay);

            return () => {
                clearTimeout(animationTimeout);
            };
        } else {
            setIsAIAnimating(false);
            // If sequential animations are active but we're not showing animation,
            // immediately call completion callback (even without video to prevent sequence from getting stuck)
            if (isSequentialAnimationActive && onAnimationComplete) {
                console.log('VideoComponent immediately completing for:', id);
                setTimeout(() => onAnimationComplete(id), 100);
            }
        }
    }, [content, tempVideoUrl, courseEditorContext?.isGeneratingChapter, courseEditorContext?.isShowingAIChapterAnimations, mode, isSequentialAnimationActive, onAnimationComplete, id]);


    const handleClick = (e) => {
        e.stopPropagation();
        if (!isSelected) {
            onSelect(id);
        }
    };

    // Double-click opens the editor drawer
    const handleDoubleClick = (e) => {
        e.stopPropagation();
        if (mode === "edit" && onOpenEditorDrawer) {
            onOpenEditorDrawer({
                id,
                type: 'video',
                content,
                borderRadius,
                backgroundColor,
                opacity
            });
        }
    };



    // Parse border radius values or use defaults
    const topLeft = borderRadius?.topLeft || 0;
    const topRight = borderRadius?.topRight || 0;
    const bottomLeft = borderRadius?.bottomLeft || 0;
    const bottomRight = borderRadius?.bottomRight || 0;

    // Create style object for the video container
    const containerStyle = {
        width: '100%',
        height: '100%',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        overflow: 'hidden',
        position: 'relative',
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
    };

    // Create style for the background with opacity
    const backgroundStyle = {
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: backgroundColor || 'transparent',
        opacity: opacity,
        borderRadius: `${topLeft}px ${topRight}px ${bottomRight}px ${bottomLeft}px`,
        zIndex: -1, // Ensure background is behind the video content
    };

    // Create style object for the video
    const videoStyle = {
        maxWidth: '100%',
        maxHeight: '100%',
        objectFit: 'contain'
    };

    return (
        <div 
            className={`VideoComponent ${isSelected ? 'selected' : ''}`}
            onClick={handleClick}
            onDoubleClick={handleDoubleClick}
        >
            <div className={`video-container ${isAIAnimating ? 'ai-image-creation' : ''}`} style={containerStyle}>
                {/* Background div with opacity */}
                <div style={backgroundStyle}></div>

                {/* Video or placeholder */}
                {content ? (
                    videoLoadError ? (
                        // Show error message when video fails to load
                        <div className="video-error">
                            <ErrorIcon fontSize="large" style={{ color: '#ff6b6b', marginBottom: '10px' }} />
                            <div className="error-message">
                                <h4>{i18n.translate('weezbeeCourse.editor.videoComponent.errorTitle', { defaultValue: 'Video Failed to Load' })}</h4>
                                <p>{i18n.translate('weezbeeCourse.editor.videoComponent.errorMessage', { defaultValue: 'The video could not be loaded. Please upload a new video or select another one from the video bank.' })}</p>
                            </div>
                            {mode === "edit" && onOpenEditorDrawer && (
                                <div className="error-actions">
                                    <button 
                                        className="edit-button"
                                        onClick={() => {
                                            // Create a component object to pass to the editor drawer
                                            const componentData = {
                                                id,
                                                type: 'video',
                                                content,
                                                borderRadius,
                                                backgroundColor,
                                                opacity
                                            };
                                            onOpenEditorDrawer(componentData);
                                        }}
                                    >
                                        <VideoLibraryIcon />
                                        <span>{i18n.translate('weezbeeCourse.editor.videoComponent.edit', { defaultValue: 'Edit' })}</span>
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : isLoadingFromBank ? (
                        // Show loading indicator when loading video from bank
                        <div className="video-loading">
                            <RefreshIcon fontSize="large" style={{ color: '#4CAF50', marginBottom: '10px', animation: 'spin 1s linear infinite' }} />
                            <span>{i18n.translate('weezbeeCourse.editor.videoComponent.loading', { defaultValue: 'Loading...' })}</span>
                        </div>
                    ) : (
                        <video 
                            src={
                                // First priority: Use the signed URL from component data (mimics background video pattern)
                                signedUrl ? signedUrl :
                                // Second priority: Use the temporary signed URL if available (for immediate display after upload)
                                tempVideoUrl ? tempVideoUrl :
                                // Third priority: Use the content if it's a URL
                                content.startsWith('http') ? content : 
                                // Fourth priority: If we're currently loading from bank, don't try to load anything yet
                                // This prevents flickering by avoiding attempts to load invalid URLs
                                isLoadingFromBank ? '' :
                                // Last resort: If content is not a URL (likely a CompanyFile ID), we still try to use it
                                // The server should have converted this ID to a signed URL in the course data
                                content
                            }
                            controls
                            style={videoStyle}
                            onEnded={() => {
                                console.log('Video ended in VideoComponent:', id);
                                // Skip if the course is already completed
                                if (courseCompleted) {
                                    console.log('Course already completed, skipping video completion tracking');
                                    return;
                                }
                                // In view mode, notify the parent component that this video has been completed
                                // This is used to track which videos have been watched in a chapter
                                // A chapter with videos is only marked as completed when all videos have been watched
                                if (mode === "view" && onVideoComplete) {
                                    onVideoComplete(id);
                                }
                            }}
                            onError={(e) => {
                                console.error("Video failed to load with content:", content);

                                // Set error state to show error message
                                setVideoLoadError(true);

                                // Prevent infinite retries - only try once per video ID
                                if (failedVideoIds.has(content)) {
                                    console.log("Video already marked as failed, not retrying:", content);
                                    return;
                                }

                                // Mark this video as failed to prevent future retries
                                setFailedVideoIds(prev => new Set([...prev, content]));

                                // If the video fails to load and content is not a URL, it might be a companyFile id or public asset id
                                // In this case, we should try to load the video from the appropriate video bank (only once)
                                if (!content.startsWith('http') && retryAttempts < 1) {
                                    console.log("Content is not a URL, likely a video ID, attempting one retry:", content);
                                    setRetryAttempts(prev => prev + 1);

                                    // Try to load the video from the appropriate video bank based on super admin mode
                                    const loadVideoFromBank = async () => {
                                        try {
                                            let video;

                                            if (isTemplateMode) {
                                                // In template mode, use the public assets API
                                                console.log("Loading video from public assets for error recovery...");
                                                const response = await getPublicAssetsAPI({
                                                    paginationModel: { page: 0, pageSize: 5 },
                                                    filterModel: {
                                                        filterValues: {
                                                            type: 'VIDEO'
                                                        }
                                                    },
                                                    sortModel: { field: 'createdAt', sort: 'desc' }
                                                });

                                                // Find the video with the matching key
                                                if (response && response.data) {
                                                    video = response.data.find(asset => asset.id === content);
                                                }
                                            } else {
                                                // In regular mode, use the company video bank API
                                                console.log("Loading video from company video bank for error recovery...");
                                                const videos = await getCompanyVideoBankAPI();
                                                video = videos.find(vid => vid.key === content);
                                            }

                                            if (video && video.signedUrl) {
                                                console.log("Found video in bank with matching key, using signed URL:", video.signedUrl);
                                                e.target.src = video.signedUrl;
                                                // Store the signed URL in tempVideoUrl for future reference
                                                setTempVideoUrl(video.signedUrl);
                                                setVideoLoadError(false);
                                                return;
                                            }
                                        } catch (error) {
                                            console.error("Error loading video from bank:", error);
                                        }

                                        console.log("Could not recover video, marking as permanently failed:", content);
                                    };
                                    loadVideoFromBank();
                                } else {
                                    console.log("Video failed to load, no retry attempted:", content);
                                }
                            }}
                        />
                    )
                ) : (
                    <div className="video-placeholder">
                        <VideoLibraryIcon fontSize="large" />
                        <span>{i18n.translate('weezbeeCourse.editor.videoComponent.placeholder', { defaultValue: 'Click to add video' })}</span>
                    </div>
                )}
            </div>

        </div>
    );
};
